"use client"

import { useState } from "react"
import { usePathname, useRouter } from "next/navigation"

import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
  useSidebar,
} from "@/components/ui/sidebar"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { Bell, ChevronRight } from "lucide-react"
import { editSessionStore } from "@/lib/edit-session-store"
import { completeScenarioEditSession } from "@/lib/api-v3"

function NavItemCollapsible({
  item,
  isMobile,
  onNavClick,
}: {
  item: { title: string; url: string; icon?: React.ComponentType<any>; items: { title: string; url: string }[] }
  isMobile: boolean
  onNavClick: (e: React.MouseEvent<HTMLAnchorElement>, url: string) => Promise<void>
}) {
  const pathname = usePathname()
  // Открываем автоматически, если текущий путь — один из под-пунктов
  const isOnSubpage = item.items.some(sub => pathname === sub.url || pathname.startsWith(sub.url + "/"))
  const [isOpen, setIsOpen] = useState(isOnSubpage)

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} asChild>
      <SidebarMenuItem>
        {/* Клик на родительский элемент только раскрывает/скрывает, не навигирует */}
        <CollapsibleTrigger asChild>
          <SidebarMenuButton
            tooltip={item.title}
            className={`rounded-lg h-9 !font-normal cursor-pointer ${isMobile ? "px-3 py-2" : ""}`}
          >
            {item.icon && <item.icon className="size-4" />}
            <span className="!font-normal flex-1">{item.title}</span>
            <ChevronRight
              className="size-4 shrink-0 transition-transform duration-200"
              style={{ transform: isOpen ? "rotate(90deg)" : undefined }}
            />
          </SidebarMenuButton>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <SidebarMenuSub>
            {item.items.map((sub) => {
              // Пункт «Обзор» совпадает с адресом раздела — для него только точное совпадение
              const subActive = pathname === sub.url || (sub.url !== item.url && pathname.startsWith(sub.url + "/"))
              return (
                <SidebarMenuSubItem key={sub.title}>
                  <SidebarMenuSubButton asChild isActive={subActive}>
                    <a
                      href={sub.url}
                      onClick={(e) => onNavClick(e, sub.url)}
                    >
                      {sub.title}
                    </a>
                  </SidebarMenuSubButton>
                </SidebarMenuSubItem>
              )
            })}
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  )
}

export function NavMain({
  items,
  notificationsHasUnread = false,
}: {
  items: {
    title: string
    url: string
    icon?: React.ComponentType<any>
    items?: { title: string; url: string }[]
  }[]
  notificationsHasUnread?: boolean
}) {
  const { isMobile, setOpenMobile } = useSidebar()
  const pathname = usePathname()
  const router = useRouter()

  const handleNavClick = async (
    e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>,
    url: string
  ) => {
    e.preventDefault()
    // Проверяем глобальный guard (устанавливается страницами с несохранёнными изменениями)
    const guard = (window as any).__navGuard as ((url: string) => boolean) | undefined
    if (guard && !guard(url)) return
    if (isMobile) setOpenMobile(false)
    const session = editSessionStore.get()
    if (session) {
      editSessionStore.clear()
      try {
        await completeScenarioEditSession(session.accessToken, session.scenarioId)
      } catch {}
    }
    router.push(url)
  }

  const notifUrl = "/notifications"
  const isNotifActive = pathname === notifUrl

  return (
    <SidebarGroup>
      <SidebarGroupContent className="flex flex-col gap-2">
        <SidebarMenu>
          {/* Пункт «Уведомления» */}
          <SidebarMenuItem>
            <SidebarMenuButton
              asChild
              tooltip="Уведомления"
              isActive={isNotifActive}
              className={`rounded-lg h-9 !font-normal ${isMobile ? "px-3 py-2" : ""}`}
              style={
                isNotifActive
                  ? { backgroundColor: "#0000000f", fontWeight: "normal" }
                  : undefined
              }
            >
              <a
                href={notifUrl}
                onClick={(e) => handleNavClick(e, notifUrl)}
                className="flex items-center gap-2 !font-normal"
              >
                <span className="relative shrink-0">
                  <Bell className="size-4" />
                  {notificationsHasUnread && (
                    <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-[#0285ff]" />
                  )}
                </span>
                <span className="!font-normal flex-1">Уведомления</span>
              </a>
            </SidebarMenuButton>
          </SidebarMenuItem>

          {items.map((item) => {
            if (item.items && item.items.length > 0) {
              return (
                <NavItemCollapsible
                  key={item.title}
                  item={item as any}
                  isMobile={isMobile}
                  onNavClick={handleNavClick}
                />
              )
            }

            const isActive =
              pathname === item.url || pathname.startsWith(item.url + "/")

            return (
              <SidebarMenuItem key={item.title}>
                <SidebarMenuButton
                  asChild
                  tooltip={item.title}
                  isActive={isActive}
                  className={`rounded-lg h-9 !font-normal ${isMobile ? "px-3 py-2" : ""}`}
                  style={
                    isActive
                      ? { backgroundColor: "#0000000f", fontWeight: "normal" }
                      : undefined
                  }
                >
                  <a
                    href={item.url}
                    onClick={(e) => handleNavClick(e, item.url)}
                    className="flex items-center gap-2 !font-normal"
                  >
                    {item.icon && <item.icon className="size-4" />}
                    <span className="!font-normal">{item.title}</span>
                  </a>
                </SidebarMenuButton>
              </SidebarMenuItem>
            )
          })}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  )
}
